import { useMediaQuery, useTheme } from '@mui/material';
import { CommunicationType, hasFlag } from '@sprytki-frontend-apps/common';
import { ILockerGateItem } from 'api/interfaces/lockerGate';
import { useTranslation } from 'react-i18next';
import { AlarmType, AlarmZoneMode, DictLockerStatus, DictPadLockMode, GateType, OpenStatus } from 'shared';
import { ItemDialogAction, OpenItemDialog } from '../dialogs/OpenItemDialog';
import { MSpaceLockDialog } from '../dialogs/MSpaceLockDialog';
import { ReleaseLockerDialog } from '../dialogs/ReleaseLockerDialog';
import { ItemDetailsDialog } from '../dialogs/itemDetails/ItemDetailsDialog';
import { PadLockDialog } from '../dialogs/padLock/PadLockDialog';
import { locationStore } from 'stores/locationStore/locationStore';
import { useState } from 'react';
import { OutOfUseDialog } from '../dialogs/outOfUse/OutOfUseDialog';
import { observer } from 'mobx-react-lite';
import { StyledActionBar, StyledActionButton } from '../StyledComponents';
import { getItemTypeLabel } from '../alerts.logic';

export type DialogType = 'openItem' | 'releaseLocker' | 'itemDetails' | 'padLock' | 'outOfUse' | 'mSpaceLock';

export interface ButtonConfig {
	label: string;
	labelShort?: string;
	onClick: () => void;
	show?: boolean;
}

export const RowItemButtons = observer((props: ILockerGateItem) => {
	const {
		id,
		userFriendlyId,
		isLocker,
		openStatus,
		gateType,
		hasAlarm,
		isArmed,
		lockerStatus,
		orderItemId,
		hasElectricLock,
		alarmType,
		padLockDetails,
		alarmZoneMode,
		linkedAlarmZoneId,
		linkedAlarmZoneMode,
		hasExtendDisarmZone,
	} = props;
	const { t } = useTranslation();
	const itemTypeLabel = getItemTypeLabel(
		{ isLocker, gateType, alarmZoneMode, orderItemTypeTag: props.lockerDetails?.orderItemTypeTag },
		t
	);

	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
	const padLockEnabled = Boolean(locationStore.selectedLocation?.padLockEnabled);
	const isPermanentlyPadLocked =
		locationStore.selectedLocation?.dictPadLockMode === DictPadLockMode.Permanent &&
		Boolean(padLockDetails?.padLockInPlace);
	const isMSpaceLock = hasFlag(
		locationStore.selectedLocation?.accessCommunicationType,
		CommunicationType.MSpaceLock as number
	);

	const [dialogs, setDialogs] = useState<{
		openItem: boolean;
		releaseLocker: boolean;
		itemDetails: boolean;
		padLock: boolean;
		outOfUse: boolean;
		mSpaceLock: boolean;
	}>({
		openItem: false,
		releaseLocker: false,
		itemDetails: false,
		padLock: false,
		outOfUse: false,
		mSpaceLock: false,
	});

	// A box under an ExtendDisarm zone offers disarming only, whatever it reads as: those zones have no
	// arming operation, and disarming again is what moves their window further out. Covers a zone of one
	// box (linked) and one spanning several (flagged), because the box's disarm reaches all of them.
	const coveredByExtendDisarmZone =
		hasExtendDisarmZone === true || linkedAlarmZoneMode === AlarmZoneMode.ExtendDisarm;

	// Which button opened the shared dialog. A box can be both opened and armed, so the dialog cannot
	// work this out from the item itself.
	const [openItemAction, setOpenItemAction] = useState<ItemDialogAction>('open');

	const openDialog = (type: DialogType) => {
		setDialogs((prev) => ({ ...prev, [type]: true }));
	};

	const openItemDialog = (action: ItemDialogAction) => {
		setOpenItemAction(action);
		openDialog('openItem');
	};

	const closeDialog = (type: DialogType) => {
		setDialogs((prev) => ({ ...prev, [type]: false }));
	};

	const { openItem, releaseLocker, itemDetails, padLock, outOfUse, mSpaceLock } = dialogs;

	const buttonsConfigs: ButtonConfig[] = [
		{
			label: t('releaseDialog.release'),
			onClick: () => openDialog('releaseLocker'),
			show: isLocker && lockerStatus === DictLockerStatus.PendingVerification && !!orderItemId,
		},
		{
			label: t('common.open'),
			onClick: () => (isMSpaceLock ? openDialog('mSpaceLock') : openItemDialog('open')),
			show:
				alarmZoneMode == null &&
				openStatus === OpenStatus.Closed &&
				(hasElectricLock || gateType === GateType.Default) &&
				alarmType !== AlarmType.Satel,
		},
		{
			label: t('common.arm'),
			onClick: () => openItemDialog('alarm'),
			show:
				isLocker &&
				hasAlarm &&
				!isArmed &&
				!isPermanentlyPadLocked &&
				// A box whose zone can only have its disarm window extended must never offer arming:
				// arming an ExtendDisarm zone is not something the system may do (ADR-014).
				!coveredByExtendDisarmZone,
		},
		{
			label: t('common.disarm'),
			onClick: () => openItemDialog('alarm'),
			show:
				alarmZoneMode == null &&
				!coveredByExtendDisarmZone &&
				(gateType === GateType.Alarm || (hasAlarm && isArmed && !isPermanentlyPadLocked)),
		},
		{
			// An ExtendDisarm zone is never armed from here, and disarming it again just moves its window
			// further out, so the box it covers offers disarming whatever state the zone is in.
			label: t('common.disarm'),
			onClick: () => openItemDialog('alarm'),
			show: alarmZoneMode == null && coveredByExtendDisarmZone && !isPermanentlyPadLocked,
		},
		{
			label: t('common.arm'),
			onClick: () => openItemDialog('alarm'),
			show: alarmZoneMode === AlarmZoneMode.FullControl && !isArmed,
		},
		{
			label: t('common.disarm'),
			onClick: () => openItemDialog('alarm'),
			show: alarmZoneMode === AlarmZoneMode.FullControl && !!isArmed,
		},
		{
			// The zone's own row: arming is not an operation it has, and disarming stays on offer even
			// while it reads as disarmed, because that is how its window gets extended.
			label: t('common.disarm'),
			onClick: () => openItemDialog('alarm'),
			show: alarmZoneMode === AlarmZoneMode.ExtendDisarm,
		},
		{
			label: t('common.buttons.putOnPadLock'),
			onClick: () => openDialog('padLock'),
			show:
				isLocker &&
				padLockEnabled &&
				!padLockDetails?.padLockInPlace &&
				lockerStatus !== DictLockerStatus.PendingVerification,
		},
		{
			label: t('common.buttons.removePadLock'),
			onClick: () => openDialog('padLock'),
			show:
				isLocker &&
				padLockEnabled &&
				padLockDetails?.padLockInPlace &&
				lockerStatus !== DictLockerStatus.PendingVerification,
		},
		{
			label: t('common.buttons.putInToUse'),
			labelShort: t('common.buttons.putInToUseShort'),
			onClick: () => openDialog('outOfUse'),
			show: isLocker && lockerStatus === DictLockerStatus.OutOfUse,
		},
		{
			label: t('common.buttons.putOutOfUse'),
			onClick: () => openDialog('outOfUse'),
			show: isLocker && lockerStatus === DictLockerStatus.Available,
		},
	];

	const primaryButtonsConfigs = buttonsConfigs.filter((x) => x.show).slice(0, 2);

	const detailsButtonConfig: ButtonConfig = {
		label: t('common.buttons.more'),
		onClick: () => openDialog('itemDetails'),
	};

	const actionButtonsConfigs = [...primaryButtonsConfigs, detailsButtonConfig];

	const actionButtons = actionButtonsConfigs.map((button, index) => (
		<StyledActionButton key={index} type='button' onClick={button.onClick}>
			{isMobile ? button.labelShort || button.label : button.label}
		</StyledActionButton>
	));

	return (
		<>
			{actionButtons.length > 0 && <StyledActionBar>{actionButtons}</StyledActionBar>}

			<OpenItemDialog
				entityId={id}
				open={openItem}
				action={openItemAction}
				hasExtendDisarmZone={coveredByExtendDisarmZone}
				isLocker={isLocker}
				hasAlarm={hasAlarm}
				isArmed={isArmed}
				gateType={gateType}
				userFriendlyId={userFriendlyId}
				typeLabel={itemTypeLabel}
				isMonitored={props.isMonitored}
				alarmZoneMode={alarmZoneMode}
				linkedAlarmZoneId={linkedAlarmZoneId}
				linkedAlarmZoneMode={linkedAlarmZoneMode}
				onClose={() => closeDialog('openItem')}
			/>

			<MSpaceLockDialog
				entityId={id}
				open={mSpaceLock}
				isLocker={isLocker}
				userFriendlyId={userFriendlyId}
				onClose={() => closeDialog('mSpaceLock')}
			/>

			<ReleaseLockerDialog
				open={releaseLocker}
				onClose={() => closeDialog('releaseLocker')}
				orderItemId={orderItemId}
				lockerId={id}
				userFriendlyId={userFriendlyId}
			/>

			<ItemDetailsDialog
				buttonsConfigs={buttonsConfigs}
				open={itemDetails}
				onClose={() => closeDialog('itemDetails')}
				lockerGateItem={props}
			/>

			{padLockDetails && (
				<PadLockDialog
					id={id}
					lockerStatus={lockerStatus}
					padLockDetails={padLockDetails}
					open={padLock}
					userFriendlyId={userFriendlyId}
					onClose={() => closeDialog('padLock')}
				/>
			)}

			<OutOfUseDialog
				open={outOfUse}
				onClose={() => closeDialog('outOfUse')}
				lockerId={id}
				lockerStatus={lockerStatus}
				userFriendlyId={userFriendlyId}
				typeLabel={itemTypeLabel}
				typeTag={props.lockerDetails?.orderItemTypeTag}
			/>
		</>
	);
});
